import React, { useState, useRef, useEffect } from 'react';
import './VideoPlayer.css';

import playIcon from '../../assets/ic_play_white.svg';
import pauseIcon from '../../assets/ic_pause.svg';
import lowVolumeIcon from '../../assets/ic_low_volume.svg';
import midVolumeIcon from '../../assets/ic_mid_volume.svg';
import mutedVolumeIcon from '../../assets/ic_muted_volume.svg';
import volumeIcon from '../../assets/ic_volume.svg';
import fullscreenIcon from '../../assets/ic_fullscreen.svg';
import settingsIcon from '../../assets/ic_settings_micro.svg';
import nextIcon from '../../assets/ic_forward.svg';
import pipIcon from '../../assets/ic_pip.svg';
import jimmy from '../../assets/jimmy.svg';
import noAdsAsset from '../../assets/no_ads_1.png';
import WatchRoom from './socket/WatchRoom';
import { useDetectAdBlock } from "adblock-detect-react";
import CountdownOverlay from './CountdownOverlay';
import PlayerSwitchHint from './PlayerSwitchHint';
import { createStallTracker } from './playbackHealth';
import SettingsMenu from './SettingsMenu';
import SkipMarkForm from './SkipMarkForm';
import { Player, PlayerOptions, loadImaSdk } from '@glomex/vast-ima-player';
import ApiService from '../../services/ApiService';
import AdBlockWarn from '../adblock/AdBlockWarn';
import { PLAYER_MODE_ANIMIX, PLAYER_MODE_ANIMIX_KODIK, PLAYER_MODE_KODIK } from './playerSources';
// Assuming assets are imported or referenced like this
import no_ads_1 from './../../assets/no_ads_1.png';
import no_ads_2 from './../../assets/no_ads_2.png';
import no_ads_3 from './../../assets/no_ads_3.png';
import no_ads_4 from './../../assets/no_ads_4.png';
import server from "./../../assets/serverIcon.svg";
import support from "./../../assets/supportIcon.svg";
import pre from "./../../assets/pre.mkv";
import ContentService from '../../services/ContentService';
import Hls from 'hls.js';
import { getKodikStreams, selectKodikStream } from './kodikStreams';


const VideoPlayer = ({ initialSrc, series, initialPrivateId, updateWatchTime, timeSec, timeSet, setTimeSet, onVideoFinished, onSeriesChange, adDetected, onNextButtonClicked, onPremium, src: srcProp, playerMode = PLAYER_MODE_ANIMIX, kodikSrc, providedKodikStreams, kodikTranslations = [], kodikTranslation = null, onKodikTranslationChange, availablePlayerModes = [], onPlayerModeChange }) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [currentTimeSec, setCurrentTimeSec] = useState(timeSec);
    const [currentTimeSec2, setCurrentTimeSec2] = useState(timeSec);
    const [currentSkipmark, setCurrentSkipmark] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isMuted, setIsMuted] = useState(() => {
        try {
            const storedMuted = localStorage.getItem("muted");
            if (storedMuted === "true" || storedMuted === "false") {
                return storedMuted === "true";
            }
        } catch {}
        const vol = parseFloat(localStorage.getItem("volume") ?? "1");
        return !isNaN(vol) ? vol === 0 : false;
    });
    const [showPre, setShowPre] = useState(localStorage.getItem("subscribed") !== "true");
    const [hasBigPlayButton, setHasBigPlayButton] = useState(false);
    const [volumeLevel, setVolumeLevel] = useState(parseFloat(localStorage.getItem("volume") ?? "1"));
    const [sliderValue, setSliderValue] = useState(0);
    const lastVolumeRef = useRef(!isNaN(volumeLevel) && volumeLevel > 0 ? volumeLevel : 1);
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [showControls, setShowControls] = useState(true);
    const [error, setError] = useState(null);
    const [screenshotStatus, setScreenshotStatus] = useState(null);
    const [adblock, setAdblock] = useState(false);
    const [adCon, setAdcon] = useState(true);
    const [showAdblock, setShowAdblock] = useState(false);
    const [settingShown, setSettingsShown] = useState(false);

    const [src, setSrc] = useState(initialSrc);
    const [privateId, setPrivateId] = useState(initialPrivateId);
    const [skipTimeout, setSkipTimeout] = useState(null);
    const [shoudShowAds, setShouldShowAds] = useState(true);
    const [showAdsTry, setShouldShowAdsTry] = useState(false);

    const [countdown, setCountdown] = useState(0);
    const [autoRetrycount, setAutoretry] = useState(0);
    const [isCountdownActive, setIsCountdownActive] = useState(false);
    const [isCountdownCancelled, setIsCountdownCancelled] = useState(false);

    const [isSeeking, setIsSeeking] = useState(false);

    const videoRef = useRef(null);
    const videoPreRef = useRef(null);
    const hlsRef = useRef(null);
    const watchRoomRef = useRef(null);
    const controlsTimeoutRef = useRef(null);
    const screenshotStatusTimeoutRef = useRef(null);
    const currentSrcRef = useRef(null);
    const rawProgressSeriesRef = useRef(null);

    const videoContainerRef = useRef(null);

    const adContainerRef = useRef(null);
    const [selectedAdUrl, setSelectedAdUrl] = useState('https://exchange.buzzoola.com/ad/12716704');


    const [skipMarks, setSkipMarks] = useState([]);
    const [noAdsImage, setNoAdsImage] = useState(false);
    const adBlockDetected = useDetectAdBlock();

    const [tooltip, setTooltip] = useState({ label: "", visible: false, x: 0 });

    const [disableAutoSkip, setDisableAutoskip] = useState(localStorage.getItem("disableAutoSkip"));
    const [savedTime, setSavedTime] = useState(0);
    const [kodikStreams, setKodikStreams] = useState([]);
    const [kodikQuality, setKodikQuality] = useState('');

    let timeoutId;
    const isKodikPlayer = playerMode === PLAYER_MODE_KODIK && Boolean(kodikSrc);
    const isAnimixKodikPlayer = playerMode === PLAYER_MODE_ANIMIX_KODIK && Boolean(kodikSrc);
    const isAnyKodikPlayer = isKodikPlayer || isAnimixKodikPlayer;
    const isKodikPlayerRef = useRef(isKodikPlayer);

    // Playback health: repeated stalls or a dead source → offer another player.
    const stallTrackerRef = useRef(null);
    if (!stallTrackerRef.current) stallTrackerRef.current = createStallTracker();
    const [switchHint, setSwitchHint] = useState(null); // 'stall' | 'error' | null
    const [switchHintDismissed, setSwitchHintDismissed] = useState(false);
    const switchHintOptions = availablePlayerModes.filter((option) => option.mode !== playerMode);
    const suggestPlayerSwitch = (reason) => {
        if (!switchHintOptions.length) return;
        setSwitchHint((current) => (current === 'error' ? current : reason));
    };

    // New episode or player: start measuring from scratch and allow the hint again.
    useEffect(() => {
        stallTrackerRef.current.reset();
        setSwitchHint(null);
        setSwitchHintDismissed(false);
    }, [series?.id, playerMode]);
    isKodikPlayerRef.current = isKodikPlayer;

    useEffect(() => {
        if (isKodikPlayer) {
            if (videoRef.current) {
                videoRef.current.pause();
                setSavedTime(videoRef.current.currentTime);
            }
            videoPreRef.current?.pause();
            setIsPlaying(false);
            setSettingsShown(false);
            setShowControls(false);
            setHasBigPlayButton(true);
            setIsLoading(false);
            setError(null);
        } else {
            setShowControls(true);
            setHasBigPlayButton(true);
        }
    }, [isKodikPlayer]);

    // Kodik RAW is a cross-origin iframe, so its currentTime is not available to
    // the page.  For an episode that also exists in Animix, keep a conservative
    // progress clock while the RAW player is visible and the preroll has ended.
    // Native Animix and AnimixLite continue to report their actual video time.
    useEffect(() => {
        if (!isKodikPlayer || series?.kodikOnly || showPre) return undefined;

        const seriesKey = String(series?.id || '');
        const savedTime = Number(timeSec) || 0;
        if (rawProgressSeriesRef.current !== seriesKey) {
            rawProgressSeriesRef.current = seriesKey;
            setCurrentTime(savedTime);
        } else {
            setCurrentTime((time) => Math.max(time, savedTime));
        }
        const intervalId = setInterval(() => {
            if (!document.hidden) setCurrentTime((time) => time + 1);
        }, 1000);

        return () => clearInterval(intervalId);
    }, [isKodikPlayer, series?.kodikOnly, showPre, timeSec]);

    useEffect(() => {
        let cancelled = false;

        if (!isAnimixKodikPlayer) {
            setKodikStreams([]);
            setKodikQuality('');
            return () => { cancelled = true; };
        }

        setShowPre(false);
        setSettingsShown(false);
        setSkipMarks([]);
        setCurrentSkipmark(null);
        setIsLoading(true);
        setError(null);
        setHasBigPlayButton(true);

        getKodikStreams(kodikSrc, providedKodikStreams)
            .then((streams) => {
                if (cancelled) return;
                if (!streams.length) throw new Error('Kodik returned no playable streams');

                const preferredQuality = localStorage.getItem('savedKodikQuality');
                const selectedStream = selectKodikStream(streams, preferredQuality);
                setKodikStreams(streams);
                setKodikQuality(selectedStream.quality);
            })
            .catch((streamError) => {
                if (cancelled) return;
                console.error('Failed to resolve Kodik streams:', streamError);
                setIsLoading(false);
                setError({
                    custom: true,
                    title: 'AnimixLite недоступен',
                    message: 'Не удалось получить видеопоток Kodik. Попробуйте Kodik RAW.',
                });
            });

        return () => { cancelled = true; };
    }, [isAnimixKodikPlayer, kodikSrc, providedKodikStreams]);

    useEffect(() => {
        if (!isAnimixKodikPlayer || !videoRef.current) return undefined;
        const selectedStream = selectKodikStream(kodikStreams, kodikQuality);
        if (!selectedStream) return undefined;

        const video = videoRef.current;
        const resumeTime = Number.isFinite(video.currentTime) ? video.currentTime : 0;
        if (resumeTime > 0) setSavedTime(resumeTime);
        hlsRef.current?.destroy();
        hlsRef.current = null;

        if (Hls.isSupported()) {
            const hls = new Hls({ enableWorker: true });
            hlsRef.current = hls;
            hls.on(Hls.Events.MANIFEST_PARSED, () => {
                setIsLoading(false);
                setError(null);
            });
            hls.on(Hls.Events.ERROR, (_, data) => {
                if (!data?.fatal) return;
                if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
                    hls.startLoad();
                    return;
                }
                if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
                    hls.recoverMediaError();
                    return;
                }
                setIsLoading(false);
                setError({ custom: true, title: 'Ошибка воспроизведения', message: 'Kodik не смог загрузить видеопоток.' });
                suggestPlayerSwitch('error');
                hls.destroy();
            });
            hls.loadSource(selectedStream.src);
            hls.attachMedia(video);
        } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
            video.src = selectedStream.src;
        } else {
            setIsLoading(false);
            setError({ custom: true, title: 'Браузер не поддерживается', message: 'В этом браузере недоступно HLS-воспроизведение.' });
        }

        return () => {
            const currentTime = Number.isFinite(video.currentTime) ? video.currentTime : 0;
            if (currentTime > 0) setSavedTime(currentTime);
            hlsRef.current?.destroy();
            hlsRef.current = null;
        };
    }, [isAnimixKodikPlayer, kodikQuality, kodikStreams]);

    const fetchSkipMarks = async () => {
        try {
            if (!series) return;
            const response = await ApiService.getSkipMarks(series.animeId, series.seriesNum, series.season);
            setSkipMarks(response.data);
         
        } catch (error) {
            console.error('Error fetching skip marks:', error);
        }
    };

    useEffect(() => {
        const loadAdUrl = async () => {
            try {
                const response = await ApiService.getVast(); // Assuming this returns the ad URL
                console.log(response.data)
                setSelectedAdUrl(response.data);
            } catch (error) {
                console.error("Error fetching ad URL:", error);
            }
        };

        loadAdUrl();
    }, []);

    const syncVolumeToElement = () => {
        const video = videoRef.current;
        if (!video) return;
        // Keep element volume in sync with slider; use muted flag for silence
        if (!isNaN(volumeLevel)) {
            video.volume = Math.max(0, Math.min(1, volumeLevel));
        }
        video.muted = isMuted;
    };

    useEffect(() => {
        // Persist muted flag and apply to element
        try { localStorage.setItem("muted", String(isMuted)); } catch {}
        syncVolumeToElement();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isMuted]);

    useEffect(() => {
        // Persist volume and apply to element
        try { localStorage.setItem("volume", String(volumeLevel)); } catch {}
        if (volumeLevel > 0) lastVolumeRef.current = volumeLevel;
        syncVolumeToElement();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [volumeLevel]);

    const togglePlayPause = () => {
        if (isKodikPlayer || !videoRef.current) return;
        var isPlaying = videoRef.current.currentTime > 0 && !videoRef.current.paused && !videoRef.current.ended
            && videoRef.current.readyState > videoRef.current.HAVE_CURRENT_DATA;
         
        if (isPlaying) {
            videoRef.current.pause();
            setIsPlaying(false);
            watchRoomRef.current?.togglePlayPause(false);
        } else {
            if (!showPre) {
                try {
                    // Respect current mute/volume preferences
                    syncVolumeToElement();
                    videoRef.current.play();
                    watchRoomRef.current?.togglePlayPause(true);
                    setIsPlaying(true);
                    setHasBigPlayButton(false);
                }
                catch (e) {
                    setIsPlaying(false);
                }
            }

        }
    };



    const handleFullScreenChange = () => {
        setIsFullScreen(document.fullscreenElement !== null);
    };

    useEffect(() => {

        // if (adDetected) {
        //     if (localStorage.getItem("subscribed") !== "true") {
        //         setAdblock(true);
        //         setIsPlaying(false);
        //         videoRef.current.pause();
        //         videoRef.current.src = "";
        //         setError({ custom: true, message: "Пожалуйста, выключите блокировщик рекламы или подключите подписку. Рекламы в плеере нет.", title: "Котёнку нечего есть" })
        //     }
        // }

        return () => {

        };
    }, [adDetected]);


    let currentSrc = "";

    useEffect(() => {
        if (isAnyKodikPlayer) return;
        setHasBigPlayButton(true);
        setAdcon(true);
        fetchSkipMarks();
        setTimeout(() => {
            console.log("show ad " + src)
            if (currentSrcRef.current !== src && src != null) {
                currentSrcRef.current = src;
                //  setupVastAdPlayer();
            }
        }, 500);
        if (shoudShowAds) {


        }
    }, [src, isAnyKodikPlayer]);

    // Keep internal state in sync when parent updates initial props
    useEffect(() => {
        if (initialSrc !== undefined) {
            setSrc(initialSrc);
        }
    }, [initialSrc]);

    useEffect(() => {
        if (initialPrivateId !== undefined) {
            setPrivateId(initialPrivateId);
        }
    }, [initialPrivateId]);

    // Backward-compat: support `src` prop if provided (e.g., TestWatchPage)
    useEffect(() => {
        if (srcProp !== undefined) {
            setSrc(srcProp);
        }
    }, [srcProp]);

    // Clear hide-controls timeout on unmount
    useEffect(() => {
        return () => {
            if (controlsTimeoutRef.current) {
                clearTimeout(controlsTimeoutRef.current);
            }
            if (screenshotStatusTimeoutRef.current) {
                clearTimeout(screenshotStatusTimeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        document.addEventListener('fullscreenchange', handleFullScreenChange);





        return () => {
            document.removeEventListener('fullscreenchange', handleFullScreenChange);
        };
    }, [currentTime, isPlaying]);

    useEffect(() => {
        // Pass current time to parent component
        updateWatchTime(currentTime);

    }, [currentTime, updateWatchTime]);

    const toggleMute = () => {
        const nowMuted = !isMuted;
        if (nowMuted) {
            // Save last non-zero volume for restoration
            if (volumeLevel > 0) lastVolumeRef.current = volumeLevel;
        } else {
            // Restoring volume if slider is at 0
            if (volumeLevel === 0) {
                const restore = lastVolumeRef.current > 0 ? lastVolumeRef.current : 1;
                setVolumeLevel(restore);
            }
        }
        setIsMuted(nowMuted);
        syncVolumeToElement();
    };

    const handleTimeUpdate = () => {
        if (!videoRef.current) return;
        const newTime = videoRef.current.currentTime;
        setCurrentTime(newTime);

        if(newTime > 10 && !isLoading && isPlaying)
        {
           setCurrentTimeSec2(newTime)
        }
        setSliderValue(newTime);


        const video = videoRef.current;

        const currentTime = video.currentTime;
        const duration = video.duration;
        const percentage = currentTime / duration;







    };





    useEffect(() => {
        const checkForSkipmark = () => {
            if (isAnyKodikPlayer || !videoRef.current) return;
            const currentTime = videoRef.current.currentTime;
            let foundSkipmark = null;

            for (let skipMark of skipMarks) {
                if (currentTime >= skipMark.startTime && currentTime <= skipMark.finishTime) {
                    foundSkipmark = skipMark;

                    if (foundSkipmark.label === "Эндинг") {
                        if (!isCountdownActive && countdown == 0 && disableAutoSkip !== "true" && localStorage.getItem("disableAutoNext") !== "true") {
                            setCountdown(5);
                            setIsCountdownActive(true); // Start the countdown and mark it as active
                        }
                    }
                    break;
                }
            }

            setCurrentSkipmark(foundSkipmark);
        };

        const interval = setInterval(checkForSkipmark, 500); // Check every 500ms

        return () => clearInterval(interval); // Cleanup the interval on unmount
    }, [skipMarks, videoRef, countdown, isCountdownActive, isAnyKodikPlayer]);

    useEffect(() => {
        if (currentSkipmark) {

            if (currentSkipmark.autoSkip && disableAutoSkip !== "true") {
                const timeout = setTimeout(() => {
                    if (videoRef.current) {
                        videoRef.current.currentTime = currentSkipmark.finishTime;
                    }
                }, 6000);

                setSkipTimeout(timeout);

                // Cleanup the timeout if the component unmounts or if currentSkipmark changes
                return () => clearTimeout(timeout);
            }

        }
    }, [currentSkipmark, videoRef]);

    const handleClick = () => {
        // Cancel the skip when the button is clicked
        if (skipTimeout) {
            clearTimeout(skipTimeout);
            setSkipTimeout(null);
        }
    };


    const handleSeek = (e) => {
        if (!videoRef.current) return;
        let newTime = e.target.value;
        if (newTime < 1) newTime = 1;
        videoRef.current.currentTime = newTime;
        setCurrentTime(newTime);
        setSliderValue(newTime);
        clearTimeout(skipTimeout);
        setSkipTimeout(null);
        if (!isSeeking) {
            watchRoomRef.current?.syncTime(newTime);
        }
    };



    const handleMouseDown = () => {
        setIsSeeking(true);
    };

    const handleMouseUp = () => {
        setIsSeeking(false);
        watchRoomRef.current?.syncTime(sliderValue);
    };

    const handleCanPlay = () => {
        if (!videoRef.current) return;
        if (isKodikPlayerRef.current) {
            videoRef.current.pause();
            return;
        }
        if (adblock) return;

        if (stallTrackerRef.current.onResume()) {
            suggestPlayerSwitch('stall');
        }
        setIsLoading(false);
        setError(null);
        setAutoretry(0);
        setIsCountdownActive(false)
        setIsCountdownCancelled(false)
        setCountdown(0)
      
        // Initialize element volume/mute from stored state
        const storedVolRaw = localStorage.getItem("volume");
        const vol = parseFloat(storedVolRaw ?? "1");
        const validVol = isNaN(vol) ? 1 : Math.max(0, Math.min(1, vol));
        setVolumeLevel(validVol);
        const storedMuted = localStorage.getItem("muted");
        const mutedFlag = storedMuted === "true" || validVol === 0;
        setIsMuted(mutedFlag);
        lastVolumeRef.current = validVol > 0 ? validVol : lastVolumeRef.current;
        // Apply immediately as well, without waiting for state effects
        if (videoRef.current) {
            videoRef.current.volume = validVol;
            videoRef.current.muted = mutedFlag;
        }
   
        if (!showPre) {
            const isPlaying = videoRef.current.currentTime > 0 && !videoRef.current.paused && !videoRef.current.ended
                && videoRef.current.readyState > videoRef.current.HAVE_CURRENT_DATA;

            if (!isPlaying) {
           
                try {

                    videoRef.current.play().catch(error => {
                        // Autoplay was prevented.
                        setIsPlaying(false);
                        setHasBigPlayButton(true);

                    });
                    setIsPlaying(true);
                    setHasBigPlayButton(false);
                    watchRoomRef.current?.syncTime(videoRef.current.currentTime);

                } catch (e) {
                    setIsPlaying(false);
                }
            }
        }
        else
        {
            if (videoPreRef.current) {
                videoPreRef.current.volume = 0.5; // keep intro quieter
            }
            videoPreRef.current.play().catch(error => {
                // Autoplay was prevented.
                setIsPlaying(false);
                setHasBigPlayButton(true);

            });
            videoRef.current.pause();
        }
        if (videoRef.current && savedTime > 0) {
            videoRef.current.currentTime = savedTime;
            setSavedTime(0); // Reset after applying
        } else if (!timeSet) { 
            // Initial load logic
            if (timeSec < videoRef.current.duration * 0.8) {
                videoRef.current.currentTime = timeSec;
            }
            setTimeSet(true);
        }
    };

    const handleVolumeChange = (e) => {
        try {
            const newVolume = parseFloat(e.target.value);
            if (isNaN(newVolume) || newVolume < 0 || newVolume > 1) {
                throw new Error('Invalid volume value');
            }
            setVolumeLevel(newVolume);
            // If slider moved above zero, unmute; if zero, mute
            setIsMuted(newVolume === 0);
        } catch (error) {
            console.error('Volume change error:', error);
            setVolumeLevel(1);
            setIsMuted(false);
        }
    };

    const toggleSettings = () => {
        setSettingsShown(!settingShown);
    }

    const toggleFullScreen = () => {
        if (!isFullScreen) {
            if (videoContainerRef.current.requestFullscreen) {
                videoContainerRef.current.requestFullscreen();
            } else if (videoContainerRef.current.mozRequestFullScreen) {
                videoContainerRef.current.mozRequestFullScreen();
            } else if (videoContainerRef.current.webkitRequestFullscreen) {
                videoContainerRef.current.webkitRequestFullscreen();
            } else if (videoContainerRef.current.msRequestFullscreen) {
                videoContainerRef.current.msRequestFullscreen();
            }
        } else {
            if (document.exitFullscreen) {
                document.exitFullscreen();
            } else if (document.mozCancelFullScreen) {
                document.mozCancelFullScreen();
            } else if (document.webkitExitFullscreen) {
                document.webkitExitFullscreen();
            } else if (document.msExitFullscreen) {
                document.msExitFullscreen();
            }
        }

    };

    const showScreenshotStatus = (message, type = 'success') => {
        setScreenshotStatus({ message, type });
        if (screenshotStatusTimeoutRef.current) {
            clearTimeout(screenshotStatusTimeoutRef.current);
        }
        screenshotStatusTimeoutRef.current = setTimeout(() => {
            setScreenshotStatus(null);
        }, 2400);
    };

    const canvasToBlob = (canvas) => new Promise((resolve, reject) => {
        try {
            canvas.toBlob((blob) => {
                if (blob) {
                    resolve(blob);
                } else {
                    reject(new Error('Canvas export returned an empty image'));
                }
            }, 'image/png');
        } catch (canvasError) {
            reject(canvasError);
        }
    });

    const waitForMediaEvent = (media, successEvent, timeoutMs = 15000) => new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
            cleanup();
            reject(new Error(`Timed out waiting for ${successEvent}`));
        }, timeoutMs);

        const cleanup = () => {
            clearTimeout(timeout);
            media.removeEventListener(successEvent, handleSuccess);
            media.removeEventListener('error', handleError);
        };
        const handleSuccess = () => {
            cleanup();
            resolve();
        };
        const handleError = () => {
            cleanup();
            reject(media.error || new Error('Video frame failed to load'));
        };

        media.addEventListener(successEvent, handleSuccess, { once: true });
        media.addEventListener('error', handleError, { once: true });
    });

    const captureFrameFromCleanVideo = async (playerVideo) => {
        const sourceUrl = playerVideo.currentSrc || src;
        if (!sourceUrl) throw new Error('Video source is unavailable');

        const isLocalDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
        const cleanSourceUrl = isLocalDev
            ? `/__animix_frame_proxy?url=${encodeURIComponent(sourceUrl)}`
            : sourceUrl;
        const cleanVideo = document.createElement('video');

        cleanVideo.preload = 'auto';
        cleanVideo.muted = true;
        cleanVideo.playsInline = true;
        if (!isLocalDev) cleanVideo.crossOrigin = 'anonymous';

        try {
            const metadataReady = waitForMediaEvent(cleanVideo, 'loadedmetadata');
            cleanVideo.src = cleanSourceUrl;
            cleanVideo.load();
            await metadataReady;

            const targetTime = Math.min(
                Math.max(playerVideo.currentTime, 0),
                Math.max((cleanVideo.duration || playerVideo.duration || 0) - 0.05, 0)
            );

            if (targetTime > 0.01) {
                const frameReady = waitForMediaEvent(cleanVideo, 'seeked');
                cleanVideo.currentTime = targetTime;
                await frameReady;
            } else if (cleanVideo.readyState < 2) {
                await waitForMediaEvent(cleanVideo, 'loadeddata');
            }

            const canvas = document.createElement('canvas');
            canvas.width = cleanVideo.videoWidth;
            canvas.height = cleanVideo.videoHeight;
            const context = canvas.getContext('2d');
            if (!context || !canvas.width || !canvas.height) {
                throw new Error('Decoded video frame is unavailable');
            }

            context.drawImage(cleanVideo, 0, 0, canvas.width, canvas.height);
            return await canvasToBlob(canvas);
        } finally {
            cleanVideo.removeAttribute('src');
            cleanVideo.load();
        }
    };

    const downloadScreenshot = (blob, playerVideo) => {
        const episodePart = series?.seriesNum !== undefined && series?.seriesNum !== null ? `-episode-${series.seriesNum}` : '';
        const timestamp = formatTime(playerVideo.currentTime).replace(/:/g, '-');
        const fileName = `animix${episodePart}-${timestamp}.png`;
        const objectUrl = URL.createObjectURL(blob);
        const downloadLink = document.createElement('a');

        downloadLink.href = objectUrl;
        downloadLink.download = fileName;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        downloadLink.remove();
        setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
        showScreenshotStatus('Стоп-кадр сохранён');
    };

    const handleScreenshot = async () => {
        const video = videoRef.current;

        if (!video || video.readyState < 2 || !video.videoWidth || !video.videoHeight) {
            showScreenshotStatus('Кадр ещё не загружен', 'error');
            return;
        }

        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        try {
            const context = canvas.getContext('2d');
            if (!context) throw new Error('Canvas is unavailable');

            context.drawImage(video, 0, 0, canvas.width, canvas.height);
            const blob = await canvasToBlob(canvas);
            downloadScreenshot(blob, video);
        } catch (captureError) {
            try {
                showScreenshotStatus('Готовим чистый стоп-кадр');
                const cleanFrameBlob = await captureFrameFromCleanVideo(video);
                downloadScreenshot(cleanFrameBlob, video);
            } catch (cleanFrameError) {
                console.error('Screenshot capture failed:', captureError, cleanFrameError);
                showScreenshotStatus('Не удалось декодировать стоп-кадр', 'error');
            }
        }
    };




    const handleMouseMove = (e) => {
        if (isKodikPlayer) return;
        // if (!showPre) {

        // }
        // else {
        //     setShowControls(false);
        // }
      
        setShowControls(true);
        if (controlsTimeoutRef.current) {
            clearTimeout(controlsTimeoutRef.current);
        }
        controlsTimeoutRef.current = setTimeout(() => {
            setShowControls(false);
        }, 3000);

        const sliderEl = document.querySelector("#range");
        if (!sliderEl || !videoRef.current) return;
        const rect = sliderEl.getBoundingClientRect();
        try {
            const posX = e.clientX - rect.left;

            const percentage = (posX / rect.width) * 100;
            const time = (percentage / 100) * videoRef.current.duration;

            for (let skipMark of skipMarks) {
                if (time >= skipMark.startTime && time <= skipMark.finishTime && e.clientY - rect.top > -20 && e.clientY - rect.top < 30) {
                    setTooltip({ label: skipMark.label, visible: true, x: posX });

                    return;
                }
            }
            setTooltip({ label: "", visible: false, x: 0 });
        }
        catch { }

    };

    const sleep = ms => new Promise(r => setTimeout(r, ms));

    const handleError = async (e) => {
        if (isAnyKodikPlayer) return;
        setAutoretry((prev) => prev + 1);
        const attempts = (autoRetrycount ?? 0) + 1;
        if (attempts >= 2) suggestPlayerSwitch('error');
    
        if (attempts < 7) {
          try {
            // Validate privateId before retry
            if (!privateId || typeof privateId !== 'string') {
              console.error('Invalid privateId during retry:', privateId);
              setError({ custom: true, message: "Ошибка декодирования видео" });
              return;
            }
    
            const srcNew = await ContentService.fetchVideo(privateId);
            if (!srcNew) {
              throw new Error('Empty video source received');
            }

            setCurrentTimeSec(currentTimeSec2);
            setSrc(srcNew);
            const trySeek = () => {
                if (videoRef.current && !isNaN(videoRef.current.duration)) {
                    videoRef.current.currentTime = currentTimeSec2;
                }
            };

            await sleep(500);
            if (videoRef.current) {
                videoRef.current.load();
                setTimeout(trySeek, 800);
            }
          } catch (error) {
            console.error('Retry failed:', error);
            setError(e || { 
              custom: true, 
              message: error.message || 'Не удалось восстановить подключение'
            });
        }
          return;
        }
        setError(e || { message: 'Превышено максимальное число попыток воспроизведения. Охладитесь' });
      };
    const handleLoading = () => {
        if (isKodikPlayer) return;
        stallTrackerRef.current.onWaiting();
        setIsLoading(true);
        setError(null);
    };

    const getErrorMessage = (error) => {
        let errorInfo = '';
        try {
            const errorCode = error.target.error.code;


            switch (errorCode) {
                case 1:
                    errorInfo = 'При обработке видео возникла проблема (corrupted source)';
                    break;
                case 2:
                    errorInfo = 'Ошибка сети. Проверьте интернет-соединение';
                    break;
                case 3:
                    errorInfo = 'Ошибка декодирования видео';
                    break;
                case 4:
                    errorInfo = 'Возможно, Вы отправляете слишком много запросов. Подождите немного и обновите страницу.';
                    break;
                default:
                    errorInfo = 'Неизвестный код ошибки: ' + errorCode;
                    break;
            }
        } catch (e) {
            errorInfo = 'Неизвестная ошибка ';
            if (error.custom != null) {
                return error.message;
            }
        }


        return errorInfo;
    }

    useEffect(() => {
            const handleKeyPress = (event) => {
                const activeElement = document.activeElement;
                const isInputFocused = activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA';

                if (!isInputFocused) {
                    if (isKodikPlayer) return;

                    switch (event.key) {
                        case 'ArrowRight':
                            videoRef.current.currentTime += 5;
                            break;
                        case 'ArrowLeft':
                            videoRef.current.currentTime -= 5;
                            break;
                        case 'ArrowUp': {
                            const step = 0.05;
                            const next = Math.min(1, (volumeLevel || 0) + step);
                            setVolumeLevel(parseFloat(next.toFixed(2)));
                            setIsMuted(next === 0);
                            event.preventDefault();
                            break;
                        }
                        case 'ArrowDown': {
                            const step = 0.05;
                            const next = Math.max(0, (volumeLevel || 0) - step);
                            setVolumeLevel(parseFloat(next.toFixed(2)));
                            setIsMuted(next === 0);
                            event.preventDefault();
                            break;
                        }
                        case 'm':
                        case 'M':
                            toggleMute();
                            event.preventDefault();
                            break;
                        case ' ':
                            togglePlayPause();
                            event.preventDefault();
                            break;
                        default:

                            break;
                    }
                }
                handleMouseMove();
            };

        document.addEventListener('keydown', handleKeyPress);

        return () => {
            document.removeEventListener('keydown', handleKeyPress);
        };
    }, [skipMarks, isKodikPlayer]);

    useEffect(() => {
        document.addEventListener('mousemove', handleMouseMove);
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
        };
    }, [skipMarks, isKodikPlayer]);

    useEffect(() => {
        const sliderEl = document.querySelector("#range");
        if (!sliderEl || !videoRef.current || !videoRef.current.duration) return;
        const progress = (sliderValue / videoRef.current.duration) * 100;
        const gradients = skipMarks.map(skipMark => {
            const startPercent = (skipMark.startTime / videoRef.current.duration) * 100;
            const finishPercent = (skipMark.finishTime / videoRef.current.duration) * 100;
            return `linear-gradient(to right, #ffffff00 ${startPercent}%,  #757575   ${startPercent}%, #757575  ${finishPercent}%, #ffffff00   ${finishPercent}%)`;
        });
        if (skipMarks.length == 0) {
            sliderEl.style.background = `linear-gradient(to right, #ffffff ${progress}%, #ffffff4d ${progress}%)`;
        }
        else {
            sliderEl.style.background = `linear-gradient(to right, #ffffff ${progress}%, #ffffff4d ${progress}%), ${gradients.join(', ')}`;
        }


    }, [sliderValue]);

    useEffect(() => {
        setIsLoading(Boolean(src));
    }, [src]);

    const formatTime = (timeInSeconds) => {
        const hours = Math.floor(timeInSeconds / 3600);
        const minutes = Math.floor((timeInSeconds % 3600) / 60);
        const seconds = Math.floor(timeInSeconds % 60);

        let formattedTime = '';

        if (hours > 0) {
            formattedTime += `${hours.toString().padStart(2, '0')}:`;
        }

        formattedTime += `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;

        return formattedTime;
    };
    useEffect(() => {
        let lastTap = 0;
        const videoElement = videoRef.current;
        if (!videoElement || isKodikPlayer) return;

        const handleTouchStart = (e) => {
            const currentTime = videoElement.currentTime;
            const timeJump = 10; // Time to jump in seconds

            const now = new Date().getTime();
            const timesince = now - lastTap;
            if (timesince < 600 && timesince > 0) {
                const tapX = e.touches[0].clientX;
                const halfWidth = window.innerWidth / 2;

                if (tapX > halfWidth) {
                    // Double tap on the right side, skip forward
                    videoElement.currentTime = currentTime + timeJump;
                } else {
                    // Double tap on the left side, skip backward
                    videoElement.currentTime = currentTime - timeJump;
                }
            } else {
                // Single tap
            }
            lastTap = now;
        };

        videoElement.addEventListener('touchstart', handleTouchStart);

        return () => {
            videoElement.removeEventListener('touchstart', handleTouchStart);
        };
    }, [isKodikPlayer]);

    const handleContextMenu = (e) => {
        e.preventDefault(); // Prevent default right-click menu
    };
    const handlePlayButton = () => {
        if (!videoRef.current) return;
        setHasBigPlayButton(false)
        try {

            if (hasBigPlayButton && showPre) {
                videoRef.current.pause();
                videoPreRef.current?.play();
            }
            else if (!isKodikPlayer) {
                syncVolumeToElement();
                videoRef.current.play();
                watchRoomRef.current?.togglePlayPause(true);
                setIsPlaying(true);
            }

        }
        catch (e) {
            setIsPlaying(false);
        }




    };

    const handleCancelCountdown = () => {
        setCountdown(0); // Reset the countdown
        setIsCountdownActive(false); // Mark the countdown as inactive
        setIsCountdownCancelled(true)
    };

    const handleSpeedChange = (speed) => {
        if (!videoRef.current) return;
        videoRef.current.playbackRate = speed;
    };

    // таймер пропуска
    useEffect(() => {
        if (countdown > 0 && isCountdownActive) {
            const countdownTimeout = setTimeout(() => {
                if (!isCountdownCancelled) {
                    setCountdown(countdown - 1);
                    
                    
                    if (countdown - 1 === 0 && !isCountdownCancelled) {
                        if (videoRef.current.readyState >= 3) { 
                            onVideoFinished();
                            setIsCountdownActive(false);
                        } else {
                            console.log("Серия ещё не загружена, ждём...");
                            setCountdown(5); 
                        }
                    }
                }
            }, 1000);
            return () => clearTimeout(countdownTimeout);
        }
    }, [countdown, isCountdownCancelled, isCountdownActive]);

    let imaPlayer;

    const setupVastAdPlayer = async () => {
        try {
            //  || Math.random() * 100 < 70
            if (localStorage.getItem("subscribed") === "true" || Math.random() * 100 < 30) return;
            if (selectedAdUrl == "no_ads") {
                videoRef.current.pause();
                setAdcon(false);
                setNoAdsImage(true);
                setTimeout(() => {
                    setNoAdsImage(false);
                    try {
                        if (!isKodikPlayerRef.current) videoRef.current?.play();
                    }
                    catch (e) { }
                }, 5000);
                return;
            }

            const ima = await loadImaSdk();




            if (window.google && window.google.ima) {
                const adsRenderingSettings = new window.google.ima.AdsRenderingSettings();
                const playerOptions = new PlayerOptions();

                // Destroy the previous player instance if exists
                if (imaPlayer) {
                    imaPlayer.destroy();
                }

                imaPlayer = new Player(
                    ima,
                    videoRef.current,
                    adContainerRef.current,
                    adsRenderingSettings,
                    playerOptions
                );

                window.google.ima.settings.setVpaidMode(window.google.ima.ImaSdkSettings.VpaidMode.INSECURE);
                window.google.ima.settings.setNumRedirects(3);

                imaPlayer.addEventListener('AdComplete', () => {
                    console.log('Ad completed');
                    setTimeout(() => {
                        // Restore user's volume/mute after ads
                        try {
                            const vol = parseFloat(localStorage.getItem("volume") ?? "1");
                            const muted = localStorage.getItem("muted") === 'true' || vol === 0;
                            const v = isNaN(vol) ? 1 : Math.max(0, Math.min(1, vol));
                            setVolumeLevel(v);
                            setIsMuted(muted);
                            if (videoRef.current) {
                                videoRef.current.volume = v;
                                videoRef.current.muted = muted;
                            }
                        } catch {}

                        setHasBigPlayButton(true);
                        setShouldShowAds(false);
                        setAdcon(false);

                    }, 500); // Adjust the delay as needed (500ms here)
                });

                imaPlayer.addEventListener('AdStopped', () => {
                    console.log('Ad stopped');


                });

                // Handle ad errors and ensure video resumes
                imaPlayer.addEventListener('AdError', () => {
                    console.log('Ad failed to play');
                    syncVolumeToElement();

                    setTimeout(() => {
                        imaPlayer.destroy();
                        console.log("bye")
                        setAdcon(false);
                    }, 3000);
                    // Attempt to play the video after an ad error
                    if (!isKodikPlayerRef.current) {
                        videoRef.current?.play().catch((error) => {
                            console.error('Play request failed:', error);
                            setHasBigPlayButton(true);
                        });
                    }
                });

                videoRef.current.muted = true;



                const playAdsRequest = new window.google.ima.AdsRequest();
                playAdsRequest.adTagUrl = selectedAdUrl;

                // Start the ad playback
                imaPlayer.playAds(playAdsRequest);
            } else {
                console.error('Google IMA SDK failed to load.');
            }
        }
        catch (e) {
            console.log('Ad failed to play');
            setHasBigPlayButton(true);
            if (setShouldShowAdsTry) {
                setShowAdblock(true);
                setHasBigPlayButton(false);
            }
            setShouldShowAdsTry(true);
            setTimeout(() => {

                setAdcon(false);
            }, 3000);
        }
    };

    const [noAdsAsset, setNoAdsAsset] = useState(no_ads_1);

    useEffect(() => {
        const noAdsAssets = [no_ads_1, no_ads_2, no_ads_3, no_ads_4];
        const randomIndex = Math.floor(Math.random() * noAdsAssets.length);
        setNoAdsAsset(noAdsAssets[randomIndex]);
    }, []);

    const handleSrcChange = (newSrc, newPrivateId) => {
        if (!newPrivateId || typeof newPrivateId !== 'string') {
          console.error('Invalid privateId in handleSrcChange:', newPrivateId);
          setError({ custom: true, message: "Неверный выбор качества." });
          return;
        }
    
        if (!newSrc || typeof newSrc !== 'string') {
          console.error('Invalid src in handleSrcChange:', newSrc);
          setError({ custom: true, message: "Неверная ссылка на источник. Обновите плеер" });
          return;
        }
        if (videoRef.current) {
            setSavedTime(videoRef.current.currentTime);
        }
        setSrc(newSrc);
        setPrivateId(newPrivateId);
      };

    const handleKodikQualityChange = (quality) => {
        if (!kodikStreams.some((stream) => stream.quality === quality)) return;
        if (videoRef.current?.currentTime > 0) setSavedTime(videoRef.current.currentTime);
        localStorage.setItem('savedKodikQuality', quality);
        setIsLoading(true);
        setKodikQuality(quality);
    };

    const handleKodikTranslationChange = async (translationId) => {
        if (!onKodikTranslationChange || String(translationId) === String(kodikTranslation?.id ?? '')) return;

        setIsLoading(true);
        setError(null);
        try {
            await onKodikTranslationChange(translationId);
        } catch (translationError) {
            setIsLoading(false);
            setError({
                custom: true,
                title: 'Не удалось сменить озвучку',
                message: 'Попробуйте выбрать другой вариант или повторить позже.',
            });
            throw translationError;
        }
    };

    useEffect(() => {
        // Example of what to do when series changes (optional)
        setSrc(initialSrc);
    }, [initialSrc]);
    const STATUS_DOMAIN = 'https://animixstatus.whitewhale.help';
    const videoDuration = videoRef.current && Number.isFinite(videoRef.current.duration)
        ? videoRef.current.duration
        : 0;
    const remainingTime = Math.max(videoDuration - currentTime, 0);

    return (
        <div
            ref={videoContainerRef}
            onContextMenu={handleContextMenu}
            className={`video-container ${isLoading ? 'loading' : ''} ${isFullScreen ? 'fullscreen' : ''}`}
        >

            {isLoading && !isKodikPlayer && <div className='fullscreeen-centered'><div className="loader">  </div></div>}

            {error != null && !isKodikPlayer && <div className='fullscreeen-centered'>
                <div className='error-con'>
                    <img src={jimmy} />
                    <div>

                        <h2>{error.custom == null ? "Произошла ошибка" : error.title}</h2>
                        <p>{getErrorMessage(error)}</p>
                        <a className='footerButton' href='https://t.me/animixlib_bot'>
                            <img src={support} alt="Support" /> Поддержка
                        </a>
                        <a className='footerButton' href={STATUS_DOMAIN}>
                            <img src={server} alt="Server Status" /> Статус серверов
                        </a>
                    </div>
                </div>
            </div>}

            {isKodikPlayer && (
                <iframe
                    key={kodikSrc}
                    className="kodik-player-frame"
                    src={kodikSrc}
                    title="Kodik RAW player"
                    frameBorder="0"
                    allowFullScreen
                    allow="autoplay *; fullscreen *"
                />
            )}
            <video

                preload='auto'

                className={`${showControls ? "video-player" : "video-player-theatre"} ${isKodikPlayer ? 'native-player-hidden' : ''}`}
                ref={videoRef}
                type='video/mp4'
                src={isAnimixKodikPlayer ? undefined : (adblock ? "" : src)}
                onClick={() => {
                    if (settingShown) {
                        setSettingsShown(false);
                        return;
                    }

                    if (!showPre) {

                        togglePlayPause();
                    }
                }}
                onPlay={() => {
                    if (isKodikPlayerRef.current) {
                        videoRef.current?.pause();
                        setIsPlaying(false);
                        return;
                    }
                    setIsPlaying(true);
                    setHasBigPlayButton(false);
                }}
                onPause={() => {
                    setIsPlaying(false);
                    if (!showPre) setHasBigPlayButton(true);
                }}
                onEnded={() => {
                    setTimeSet(false);
                    setIsPlaying(false);
                    setHasBigPlayButton(true);
                }}
                onTimeUpdate={handleTimeUpdate}
                onCanPlay={handleCanPlay}
                onWaiting={handleLoading}
                onSeeking={() => stallTrackerRef.current.onSeek()}

                onError={handleError}
                playsInline
            />

            {
                showPre ?
                    <video
                        preload="auto"
                        className="video-player-pre"
                        ref={videoPreRef}
                        type="video/mp4"

                        src={pre}
                        onPlay={() => setHasBigPlayButton(false)}
                        onClick={(event) => {
                            event.currentTarget.play().catch(() => setHasBigPlayButton(true));
                        }}
                        onEnded={() => {
                            setShowPre(false);
                            setShowControls(true);
                            setHasBigPlayButton(true);
                        }}
                        onError={() => {
                            setShowPre(false);
                            setShowControls(true);
                            setHasBigPlayButton(true);
                        }}
                    />

                    :
                    ""
            }
            {(!isKodikPlayer || showPre) && <div className={`fullscreeen-centered-bpb ${hasBigPlayButton ? 'is-visible' : 'is-hidden'} ${showPre ? 'preroll-play-overlay' : ''}`}>
                <button className='big-play-button' onClick={handlePlayButton} aria-label="Воспроизвести">
                    <img src={playIcon} alt="" />
                </button>
            </div>}
            {!isKodikPlayer && <div className={showControls ? "controls" : "controls-hide"}>
                <div className="player-control-dock">
                    <div className="player-progress-row">
                        <span className="time-text">{formatTime(currentTime)}</span>
                        <input
                            type="range"
                            id="range"
                            min="0"
                            max={videoDuration}
                            value={sliderValue}
                            onChange={handleSeek}
                            onMouseDown={handleMouseDown}
                            onMouseUp={handleMouseUp}
                            className={isFullScreen ? "controls-input player-timeline fullscreen" : "controls-input player-timeline"}
                            aria-label="Позиция воспроизведения"
                        />
                        <span className="time-text time-text-remaining">−{formatTime(remainingTime)}</span>

                        <div className={`tooltip-video ${(!tooltip.visible || !showControls) ? "hide" : ""}`}>
                            {tooltip.label}
                        </div>
                    </div>

                    <div className="player-actions-row">
                        <div className="player-actions-group">
                            <button className='controls-button controls-button-primary' onClick={togglePlayPause} aria-label={isPlaying ? 'Пауза' : 'Воспроизвести'}>
                                <img className='img-button' src={isPlaying ? pauseIcon : playIcon} alt="" />
                            </button>
                            <button className='controls-button' onClick={onNextButtonClicked} aria-label="Следующая серия">
                                <img className='img-button' src={nextIcon} alt="" />
                            </button>
                            <div className="volume-control-group">
                                <button className='controls-button' onClick={toggleMute} aria-label={isMuted ? 'Включить звук' : 'Выключить звук'}>
                                    <img
                                        className='img-button'
                                        src={isMuted ? mutedVolumeIcon : (volumeLevel === 0 ? mutedVolumeIcon : (volumeLevel <= 0.33 ? lowVolumeIcon : (volumeLevel <= 0.66 ? midVolumeIcon : volumeIcon)))}
                                        alt=""
                                    />
                                </button>
                                <div className="volume-controls">
                                    <input
                                        type="range"
                                        min={0}
                                        max={1}
                                        step={0.01}
                                        value={volumeLevel}
                                        onChange={handleVolumeChange}
                                        className="controls-input volume-slider"
                                        aria-label="Громкость"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="player-actions-group player-actions-group-right">
                            <button className='controls-button' onClick={handleScreenshot} aria-label="Сохранить стоп-кадр" title="Сохранить стоп-кадр">
                                <svg className="player-control-icon" viewBox="0 0 24 24" aria-hidden="true">
                                    <path d="M8.2 5.5 9.4 4h5.2l1.2 1.5H19A2.5 2.5 0 0 1 21.5 8v9A2.5 2.5 0 0 1 19 19.5H5A2.5 2.5 0 0 1 2.5 17V8A2.5 2.5 0 0 1 5 5.5h3.2Z" />
                                    <circle cx="12" cy="12.5" r="3.5" />
                                </svg>
                            </button>
                            <button className='controls-button' onClick={() => {
                                if (document.pictureInPictureElement) {
                                    document.exitPictureInPicture();
                                } else if (videoRef.current) {
                                    videoRef.current.requestPictureInPicture().catch(console.error);
                                }
                            }} aria-label="Картинка в картинке">
                                <img className='img-button' src={pipIcon} alt="" />
                            </button>
                            <button className={`controls-button ${settingShown ? 'is-active' : ''}`} onClick={toggleSettings} aria-label="Настройки">
                                <img className='img-button' src={settingsIcon} alt="" />
                            </button>
                            <button className='controls-button' onClick={toggleFullScreen} aria-label={isFullScreen ? 'Выйти из полноэкранного режима' : 'На весь экран'}>
                                <img className='img-button' src={fullscreenIcon} alt="" />
                            </button>
                        </div>
                    </div>
                </div>
                {screenshotStatus && (
                    <div className={`player-capture-toast ${screenshotStatus.type === 'error' ? 'is-error' : ''}`} role="status">
                        {screenshotStatus.message}
                    </div>
                )}
            </div>}
            {currentSkipmark != null ? (
                (!isAnyKodikPlayer && (currentSkipmark.autoSkip && skipTimeout != null || !currentSkipmark.autoSkip)) ?
                    <div className={`skip-button ${skipTimeout ? 'filling' : ''}`} onClick={() => {
                        if (!currentSkipmark.autoSkip) {
                            if (videoRef.current) {
                                videoRef.current.currentTime = currentSkipmark.finishTime;
                            }
                        }
                        handleClick();
                    }}>
                        {currentSkipmark.autoSkip ? "Смотреть" : "Пропустить"} {currentSkipmark.label}

                    </div> : null
            ) : null}

            {switchHint && !switchHintDismissed && onPlayerModeChange ? (
                <PlayerSwitchHint
                    reason={switchHint}
                    options={switchHintOptions}
                    onSwitch={(mode) => {
                        setSwitchHint(null);
                        onPlayerModeChange(mode);
                    }}
                    onDismiss={() => setSwitchHintDismissed(true)}
                />
            ) : null}

            {!isAnyKodikPlayer && <CountdownOverlay
                text={"Следующая серия начнётся"}
                isCountdownActive={isCountdownActive}
                countdown={countdown}
                setCountdown={setCountdown}
                onCancel={() => {
                    setIsCountdownCancelled(true);
                    setIsCountdownActive(false);
                }}
            />}

            {/* {adCon ? <a className='adcon' id='ad-container-vast' ref={adContainerRef}  ></a> : ''} */}
            {noAdsImage ? <img className='noadcon' src={noAdsAsset} alt="No ads available" /> : ''}

            <SettingsMenu videoRef={videoRef} series={series} visible={settingShown && showControls && !isKodikPlayer} onSpeedChanged={handleSpeedChange} debugInfo={{ src: isAnimixKodikPlayer ? selectKodikStream(kodikStreams, kodikQuality)?.src : src, currentTime, isFullScreen, error, isLoading, volumeLevel, adDetected, playerMode, kodikSrc }}
                playbackProvider={isAnimixKodikPlayer ? 'kodik' : 'animix'} externalQualities={kodikStreams} externalQuality={kodikQuality} onExternalQualityChanged={handleKodikQualityChange}
                externalTranslations={kodikTranslations} externalTranslation={kodikTranslation} onExternalTranslationChanged={handleKodikTranslationChange}
                onSrcChanged={handleSrcChange} onPremium={onPremium} />

            {!isKodikPlayer && <WatchRoom ref={watchRoomRef} isFullScreen={isFullScreen} isHidden={!showControls} currentTime={currentTime} videoRef={videoRef} onSeriesChange={onSeriesChange} />}
            {/* {showAdblock ? <AdBlockWarn forceShow={true} /> : ''} */}

        </div>
    );
};

export default VideoPlayer;
